import { useState, useRef, useEffect, type ReactNode } from 'react';

export interface DropdownProps {
  /** Element that toggles the dropdown */
  trigger: ReactNode;
  /** Dropdown menu content */
  children: ReactNode;
  /** Menu placement relative to trigger */
  placement?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  /** Additional CSS class names */
  className?: string;
  /** Disabled state — prevents opening */
  disabled?: boolean;
}

const fadeInKeyframes = `
@keyframes dropdownFadeIn {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;

// Inject keyframes once
let stylesInjected = false;
function injectStyles() {
  if (stylesInjected) return;
  const style = document.createElement('style');
  style.textContent = fadeInKeyframes;
  document.head.appendChild(style);
  stylesInjected = true;
}

function getMenuPositionStyle(
  placement: NonNullable<DropdownProps['placement']>,
): React.CSSProperties {
  const isTop = placement.startsWith('top');
  const isRight = placement.endsWith('right');

  return {
    position: 'absolute',
    ...(isTop
      ? { bottom: 'calc(100% + 4px)' }
      : { top: 'calc(100% + 4px)' }),
    ...(isRight ? { right: 0 } : { left: 0 }),
    minWidth: '220px',
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
    border: '1px solid #e5e7eb',
    padding: '4px',
    zIndex: 1000,
    animation: 'dropdownFadeIn 0.15s ease-out',
  };
}

export function Dropdown({
  trigger,
  children,
  placement = 'bottom-left',
  className,
  disabled,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Inject fade-in animation keyframes on first render
  useEffect(() => {
    injectStyles();
  }, []);

  // Close on click outside
  useEffect(() => {
    if (!open) return;

    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleToggle = () => {
    if (!disabled) setOpen((prev) => !prev);
  };

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <div onClick={handleToggle} style={{ cursor: disabled ? 'default' : 'pointer' }}>
        {trigger}
      </div>
      {open && (
        <div className="dropdown-menu" style={getMenuPositionStyle(placement)}>
          {children}
        </div>
      )}
    </div>
  );
}

export default Dropdown;
